import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "button-component inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all  disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "text-[rgb(206,255,62)] bg-black hover:bg-black/90",
        defaultLight: "text-black bg-club-lime hover:bg-club-lime/90",
        blue: "bg-remedium-blue text-white hover:bg-remedium-blue-light",
        destructive: "bg-rose-600 text-white hover:bg-rose-600/90",
        success: "bg-teal-600 text-white hover:bg-teal-600/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        destructiveHover: "hover:bg-rose-600 hover:text-white",
        neutral: "",
        virtualOffice:
          "bg-virtual-office-green text-black hover:bg-virtual-office-green/80",
        virtualOfficeReverse:
          "text-virtual-office-green bg-zinc-800 hover:bg-zinc-900",
        outlineVirtualOffice:
          "border-virtual-office-green border text-virtual-office-green hover:bg-virtual-office-green/80 hover:text-black",
        vehis: "bg-[#483A4F] text-white hover:bg-[#5E3D6A]",
        outlineVehis:
          "border-[#483A4F] border text-[#483A4F] hover:bg-[#483A4F]/10 hover:text-[#483A4F]",
        aliorLeasing: "bg-[#6B1D5E] text-white hover:bg-[#7E2470]",
        outlineAliorLeasing:
          "border-[#6B1D5E] border text-[#6B1D5E] hover:bg-[#6B1D5E]/10 hover:text-[#6B1D5E]",
        link: "text-foreground underline-offset-4 hover:underline",
        inline:
          "inline-flex items-center justify-start gap-2 text-remedium-blue hover:underline",
        outlineWhite:
          "border border-white bg-transparent text-white hover:bg-white/10",
        outlinePrimary:
          "border border-remedium-blue bg-remedium-blue/2.5 text-black hover:bg-remedium-blue/5 text-remedium-blue [&_path]:stroke-remedium-blue",
        gradient:
          "bg-gradient-to-r from-blue-600 to-red-500 text-white hover:from-blue-700 hover:to-red-600",
        white: "bg-white text-black hover:bg-gray-100",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3 text-xs",
        lg: "h-10 px-8",
        icon: "h-10 w-10",
        inline: "h-fit px-1 py-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];
export type ButtonSize = VariantProps<typeof buttonVariants>["size"];

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const isDarkVariant = [
      "default",
      "blue",
      "destructive",
      "vehis",
      "aliorLeasing",
    ].includes(variant ?? "default");
    return (
      <Comp
        className={cn(
          "flex items-center justify-center will-change-transform hover:scale-102 active:scale-98",
          "cursor-pointer",
          "[&_svg]:h4 [&_svg]:w-4",
          variant === "default" || !variant
            ? "[&_path]:stroke-lime [&_svg]:mt-0.75"
            : variant === "destructiveHover"
              ? "hover:[&_path]:stroke-white"
              : isDarkVariant
                ? "[&_path]:stroke-white"
                : "[&_path]:stroke-black",
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
